import React, { useEffect, useState } from "react";
import { Card, Spinner, Alert } from "react-bootstrap";
import { fetchObjectifMois } from "./DashboardService";
import { construireJauge, ecart, euros, nbJours, COULEUR_PREVISIONNEL } from "./objectifMoisJauge";

/**
 * Où en est le mois : objectif de CA, et charges couvertes.
 *
 * Le dashboard disait ce qu'il fallait faire aujourd'hui, jamais où on en était
 * du mois. L'objectif existait pourtant déjà, mais seulement dans le récap
 * mensuel — donc lisible le 1er du mois suivant, quand il est trop tard.
 *
 * **Tout en HT, une seule unité pour la carte entière.** La première version
 * donnait l'objectif en TTC et les charges en HT : entre les deux, la ligne « il
 * manque X » ne se rattachait à aucun des chiffres affichés, et la carte était
 * illisible pour cette seule raison.
 *
 * HT parce que c'est le sens comptable de « chiffre d'affaires » — la TVA est
 * collectée pour l'État, ce n'est pas une recette —, parce que c'est l'unité des
 * charges fixes, et parce que c'est la seule qui n'exige aucune conversion : le
 * HT est lu en base, jamais déduit d'une division par 1,20. Le taux, ici, vit par
 * commande dans la colonne `TVA` et n'a pas de définition globale : en écrire une
 * pour l'affichage aurait créé un nombre de plus à maintenir.
 *
 * L'API n'envoie même plus le TTC, ce qui rend le mélange impossible à
 * réintroduire ici par inadvertance.
 *
 * **Le repère de rythme est ce qui rend la barre lisible.** Sans lui, 12 000 €
 * le 12 du mois est un chiffre sans verdict, et la jauge reste rouge toute la
 * première quinzaine sans rien apprendre. Le repère compte les jours
 * *livrables* — samedi compris, dimanches et jours fériés exclus —, calculés au
 * serveur.
 *
 * Aucun nombre n'est écrit ici : l'objectif, les pourcentages et la projection
 * viennent tous de l'API. C'est ce qui a mal tourné pour l'objectif de 5 000 €
 * par livreur, recopié dans deux écrans et deux fichiers PHP.
 */

// Cinq minutes, pas trente secondes comme les clients en ligne : un CA mensuel
// ne bouge pas à la seconde, et cet appel somme deux mois de commandes.
const RAFRAICHISSEMENT_MS = 5 * 60 * 1000;

const pourcent = (valeur) =>
  valeur === null || valeur === undefined
    ? "—"
    : `${Number(valeur).toFixed(0)} %`;

/** Le jour d'un horodatage « 2026-09-27 18:32:00 », en 27/09. */
const jour = (horodatage) => {
  // Decoupage de la chaine plutot que `new Date(...)` : le format « Y-m-d H:i:s »
  // avec une espace n'est pas garanti par la specification, et Safari le refuse.
  const date = String(horodatage ?? "").slice(0, 10).split("-");

  return date.length === 3 ? `${date[2]}/${date[1]}` : "";
};

const borner = (part) => Math.min(100, Math.max(0, part ?? 0));

/**
 * Une jauge unique, ses zones de remplissage et les seuils posés dessus.
 *
 * **Une seule barre, donc un seul axe en euros.** La version précédente en avait
 * deux — une pour l'objectif, une pour les charges — ce qui obligeait à écrire le
 * CA deux fois, une par barre. Le même nombre à deux endroits se lit comme deux
 * mesures différentes.
 *
 * **Le remplissage est découpé en zones**, et non peint d'une seule couleur :
 * jusqu'aux charges, puis jusqu'à l'objectif, puis au-delà. Une couleur unique ne
 * disait que l'état ; les zones disent la composition — quelle part du chiffre
 * d'affaires part en charges, quelle part est de la marge. Chaque zone porte la
 * couleur de son repère, si bien que les étiquettes sous la barre tiennent lieu
 * de légende.
 *
 * L'axe est calculé sur le plus grand des trois montants : dépasser l'objectif ne
 * fait jamais sortir la barre, et l'objectif devient alors un repère au lieu
 * d'être la fin de la barre.
 */
const Jauge = ({ segments, seuils }) => (
  <div>
    <div
      style={{
        position: "relative",
        height: 24,
        background: "#e9ecef",
        // Les coins sont arrondis par le cadre, qui découpe les zones : leur
        // donner un rayon chacune dessinerait des encoches à chaque jointure.
        borderRadius: 3,
        overflow: "hidden",
        display: "flex",
      }}
    >
      {segments.map((segment) => (
        <div
          key={segment.cle}
          style={{
            width: `${borner(segment.part)}%`,
            height: "100%",
            // `fond` quand la zone n'est pas un aplat : le prévisionnel, hachuré.
            background: segment.fond || segment.couleur,
            transition: "width .3s",
          }}
        />
      ))}

      {seuils.map((seuil) => (
        // Dessiné PAR-DESSUS les zones, jamais dedans : à l'intérieur d'une barre
        // pleine un repère disparaîtrait, et c'est justement quand on dépasse un
        // seuil qu'on veut voir où il était.
        //
        // Blanc cerné de noir translucide : c'est le seul habillage qui se
        // détache à la fois du gris du cadre et des trois couleurs de zones. Un
        // trait coloré se confondrait avec la zone qu'il borne.
        <div
          key={seuil.cle}
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            // `- 1px` pour centrer le trait sur sa valeur, et surtout pour qu'un
            // seuil situé au maximum de l'axe reste visible : à `left: 100%` ses
            // 2 px tombent entièrement hors du cadre, que `overflow: hidden`
            // découpe — le repère disparaîtrait exactement quand il est atteint.
            left: `calc(${borner(seuil.part)}% - 1px)`,
            width: 2,
            background: "#ffffff",
            boxShadow: "0 0 0 1px rgba(0,0,0,.35)",
          }}
          title={seuil.infobulle}
        />
      ))}
    </div>

    {/* Les étiquettes, sous leur repère. Position bornée à [6, 94] % : le trait
        reste exact, mais une étiquette centrée sur 2 % sortirait de la carte. */}
    <div style={{ position: "relative", height: 16, marginTop: 3 }}>
      {seuils.map((seuil) => (
        <div
          key={seuil.cle}
          style={{
            position: "absolute",
            left: `${Math.min(94, Math.max(6, seuil.part ?? 0))}%`,
            transform: "translateX(-50%)",
            whiteSpace: "nowrap",
            fontSize: 10,
            color: seuil.couleur,
          }}
          title={seuil.infobulle}
        >
          {seuil.libelle}
        </div>
      ))}
    </div>
  </div>
);

const ObjectifMois = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailOuvert, setDetailOuvert] = useState(false);
  // Carte repliable, état retenu par navigateur : une préférence d'affichage,
  // pas une donnée. try/catch : le stockage peut être indisponible.
  const [replie, setReplie] = useState(() => {
    try {
      return localStorage.getItem("objectifMoisReplie") === "1";
    } catch {
      return false;
    }
  });
  const basculer = () => {
    setReplie((v) => {
      try {
        localStorage.setItem("objectifMoisReplie", v ? "0" : "1");
      } catch {
        // préférence non retenue, sans conséquence
      }
      return !v;
    });
  };

  useEffect(() => {
    let annule = false;

    const charger = async () => {
      try {
        const resultat = await fetchObjectifMois();
        if (!annule) {
          setData(resultat);
          setError(null);
        }
      } catch (e) {
        console.error("Erreur chargement objectif du mois:", e);
        if (!annule) setError("Impossible de charger l'objectif du mois");
      } finally {
        if (!annule) setLoading(false);
      }
    };

    charger();
    const interval = setInterval(charger, RAFRAICHISSEMENT_MS);

    return () => {
      annule = true;
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="warning" className="py-2 mb-0">
        {error}
      </Alert>
    );
  }

  if (!data?.success) return null;

  const {
    mois,
    ca,
    rythme,
    annee_derniere: anneeDerniere,
    charges,
    estimation,
  } = data;

  // Tout le calcul de la jauge vit dans objectifMoisJauge.js : il est ainsi
  // exécutable hors navigateur, donc vérifiable. Deux des défauts trouvés à
  // l'écriture — barre rouge sans charges saisies, repère « objectif 0 € » — ne
  // se voyaient dans aucun cas d'usage normal.
  const jauge = construireJauge({ ca, rythme, charges });
  const {
    segments,
    seuils,
    previsionnel,
    objectifDansLaBarre,
    chargesConnues,
    chargesCouvertes,
    dansLeRythme,
  } = jauge;

  return (
    <Card className="shadow-sm">
      <Card.Body>
        {/* L'unité est annoncée UNE fois, pour la carte entière. La répéter sur
            chaque montant alourdit, ne la dire nulle part laisse deviner — et
            c'est ce qui rendait la version précédente incompréhensible. */}
        <button
          type="button"
          onClick={basculer}
          aria-expanded={!replie}
          className="btn btn-link p-0 text-decoration-none w-100 d-flex justify-content-between align-items-center text-uppercase text-muted"
          style={{ fontSize: 12, fontWeight: 600, letterSpacing: ".04em", marginBottom: replie ? 0 : 16 }}
        >
          <span>
            {mois.libelle} · montants HT
            {/* Replié : l'essentiel sur une ligne, sans ouvrir la carte. */}
            {replie && (
              <span className="text-dark ms-2" style={{ textTransform: "none", letterSpacing: 0 }}>
                · <strong>{euros(ca.ht)}</strong>
                {previsionnel > 0 && <> + {euros(previsionnel)} prévus</>}
                {" · "}{pourcent(ca.part_objectif)} de l'objectif
              </span>
            )}
          </span>
          <span aria-hidden="true">{replie ? "▾" : "▴"}</span>
        </button>

        {!replie && (
        <>

        {/* ── Le CA, dit UNE SEULE FOIS ──────────────────────────────────── */}
        {/* Il apparaissait deux fois, une par barre, chacune bâtie autour de son
            propre montant. Le même nombre à deux endroits se lit comme deux
            mesures : c'était le dernier reste du mélange, sous une autre forme.
            Le CA est désormais le sujet, et l'objectif comme les charges ne sont
            que des repères sur le même axe. */}
        <div className="d-flex justify-content-between align-items-end mb-2">
          <div>
            <span style={{ fontSize: 28, fontWeight: 700 }}>
              {euros(ca.ht)}
            </span>
            {/* Le prévisionnel, à côté du réalisé et jamais additionné à lui :
                le montant affiché en gros reste ce qui est livré. La pastille
                hachurée renvoie au segment de la barre ; le texte est plus foncé
                que le segment, trop pâle pour être lu. */}
            {previsionnel > 0 && (
              <span
                className="ms-2"
                style={{ fontSize: 15, fontWeight: 600, color: "#3d8bfd" }}
                title={`${ca.previsionnel_nb} commande${ca.previsionnel_nb > 1 ? "s" : ""} pas encore livrée${ca.previsionnel_nb > 1 ? "s" : ""}, livraison prévue en ${String(mois.libelle).split(" ")[0]}`}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    marginRight: 4,
                    borderRadius: 2,
                    background: `repeating-linear-gradient(135deg, ${COULEUR_PREVISIONNEL} 0 3px, #cfe2ff 3px 6px)`,
                  }}
                />
                + {euros(previsionnel)} prévus
              </span>
            )}
            <span className="text-muted ms-2" style={{ fontSize: 12 }}>
              au {jour(mois.arrete_au)} · {nbJours(rythme.jours_livrables_ecoules)}/
              {nbJours(rythme.jours_livrables_total)} jours travaillés
            </span>
          </div>
          {!objectifDansLaBarre && (
            // L'objectif est la fin de la barre : on le nomme ici plutôt qu'en
            // repère, où son étiquette sortirait du cadre.
            <div className="text-muted" style={{ fontSize: 13 }}>
              objectif {euros(ca.objectif_ht)}
            </div>
          )}
        </div>

        <Jauge segments={segments} seuils={seuils} />

        {/* Le verdict, dans l'ordre où les étapes comptent : couvrir les charges,
            puis tenir le rythme. Une seule phrase, celle qui s'applique. */}
        <div className="mt-2" style={{ fontSize: 13 }}>
          {chargesConnues && !chargesCouvertes ? (
            <span className="text-danger">
              <strong>Manque {euros(ecart(charges.total_ht, ca.ht))}</strong>{" "}
              pour couvrir les charges du mois
            </span>
          ) : chargesConnues ? (
            <span className={dansLeRythme ? "text-success" : "text-warning"}>
              <strong>Charges couvertes</strong> · marge{" "}
              {euros(ecart(ca.ht, charges.total_ht))} ·{" "}
              {pourcent(ca.part_objectif)} de l'objectif
            </span>
          ) : (
            <span className={dansLeRythme ? "text-success" : "text-warning"}>
              <strong>{pourcent(ca.part_objectif)}</strong> de l'objectif
            </span>
          )}
          {chargesConnues && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 ms-2"
              style={{ fontSize: 12 }}
              onClick={() => setDetailOuvert((v) => !v)}
            >
              détail des charges {detailOuvert ? "▴" : "▾"}
            </button>
          )}
        </div>

        {detailOuvert && chargesConnues && (
          <div className="mt-2 ps-1" style={{ fontSize: 12 }}>
            {charges.detail.map((c) => (
              <div
                key={`${c.categorie}-${c.libelle}`}
                className="d-flex justify-content-between text-muted"
              >
                <span>{c.libelle}</span>
                <span>{euros(c.montant_ht)}</span>
              </div>
            ))}
            {/* Variables engagées à date : taux × activité du mois jusqu'ici
                (ChargesMensuellesService). Absentes d'une API plus ancienne. */}
            {(charges.variables ?? []).map((v) => (
              <div
                key={`v-${v.id ?? v.libelle}`}
                className="d-flex justify-content-between text-muted"
              >
                <span>
                  {v.libelle} <em style={{ fontSize: 11 }}>(variable, à date)</em>
                </span>
                <span>{v.montant_ht === null ? "N/D" : euros(v.montant_ht)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Combien de commandes il reste a faire. « Manque 2 718 EUR » ne se
            traduit pas tout seul en decision ; un nombre de commandes et une
            cadence quotidienne, si.

            Tout vient du serveur, y compris les arrondis : le nombre de commandes
            est arrondi au SUPERIEUR, parce qu'il manque 2,1 commandes veut dire
            qu'il en faut 3. Le refaire ici au plus proche annoncerait un equilibre
            atteint avec un nombre qui ne le couvre pas. */}
        {/* `estimation &&` d'abord, et ce n'est pas une precaution decorative :
            si le back-office est deploye avant l'API, le bloc est absent de la
            reponse, et `undefined !== null` vaut VRAI — la carte entiere plantait
            sur la ligne suivante. */}
        {estimation &&
          (estimation.commandes_charges !== null ||
            estimation.commandes_objectif !== null) && (
          <div className="mt-2" style={{ fontSize: 12 }}>
            {estimation.commandes_charges !== null && (
              <div className="text-muted">
                ≈ <strong>{estimation.commandes_charges} commandes</strong> pour
                couvrir les charges
                {estimation.par_jour_charges !== null && (
                  <>
                    {" "}
                    · {estimation.par_jour_charges} / jour sur{" "}
                    {nbJours(estimation.jours_restants)} jour
                    {estimation.jours_restants > 1 ? "s" : ""} restant
                    {estimation.jours_restants > 1 ? "s" : ""}
                  </>
                )}
              </div>
            )}

            {estimation.commandes_objectif !== null && (
              <div className="text-muted">
                ≈ <strong>{estimation.commandes_objectif} commandes</strong> pour
                atteindre l'objectif
                {/* Quand l'objectif est hors d'atteinte, cette cadence est
                    absurde au regard du rythme reel — et c'est l'information.
                    Ni masquee, ni plafonnee. */}
                {estimation.par_jour_objectif !== null && (
                  <> · {estimation.par_jour_objectif} / jour</>
                )}
              </div>
            )}

            {/* Le panier moyen est affiche pour que le nombre de commandes ne soit
                pas une boite noire, et « estimation » pour ce que le mot dit : la
                moyenne lisse des livraisons flex, planifiees et urgentes, de poids
                tres differents. */}
            <div className="text-muted" style={{ fontSize: 11 }}>
              estimation au panier moyen du mois
              {estimation.panier_moyen_ht !== null && (
                <> : {euros(estimation.panier_moyen_ht)}</>
              )}
              {/* Pour les charges, une commande compte pour son panier MOINS son
                  coût variable : sinon le nombre annoncé est trop optimiste. */}
              {estimation.cout_variable_commande > 0 && (
                <>
                  {" "}
                  · coût variable d'une commande{" "}
                  {euros(estimation.cout_variable_commande)}
                </>
              )}
            </div>
          </div>
        )}

        {estimation?.contribution_negative && (
          <div className="text-danger mt-2" style={{ fontSize: 12 }}>
            Une commande moyenne coûte plus en charges variables qu'elle ne
            rapporte : aucun nombre de commandes ne couvre les charges. Vérifiez
            les taux dans « Charges mensuelles ».
          </div>
        )}

        {!chargesConnues && (
          // Masqué tant que le total vaut 0 : un repère à zéro ne dirait pas que
          // les montants manquent, il dirait que rien ne coûte rien.
          <div className="text-muted mt-2" style={{ fontSize: 12 }}>
            Charges non renseignées — à saisir dans « Charges mensuelles ».
          </div>
        )}

        <div className="text-muted mt-2" style={{ fontSize: 13 }}>
          {ca.projection_ht !== null ? (
            <>Au rythme actuel : <strong>{euros(ca.projection_ht)}</strong> en fin de mois</>
          ) : (
            // Ni « 0 € » ni une extrapolation : trop tôt dans le mois, une
            // projection sur deux jours ferait prendre une décision fausse.
            <>Projection disponible après 3 jours travaillés</>
          )}
          {anneeDerniere.evolution !== null ? (
            <>
              {" · "}l'an dernier {euros(anneeDerniere.ca_ht)} sur la même
              durée{" "}
              <span
                className={
                  anneeDerniere.evolution >= 0 ? "text-success" : "text-danger"
                }
              >
                ({anneeDerniere.evolution >= 0 ? "+" : ""}
                {anneeDerniere.evolution} %)
              </span>
            </>
          ) : (
            // `null` veut dire « aucune donnée l'an dernier », pas « stable ».
            // Même règle que les trois récaps ops.
            <> · pas de référence l'an dernier</>
          )}
        </div>

        {/* Dit une fois, pour que l'écart avec le terrain ne passe pas pour un
            bug : une livraison faite mais pas encore marquée « Livrée » n'est
            pas comptée. */}
        <div className="text-muted mt-3" style={{ fontSize: 11 }}>
          Montants <strong>hors taxes</strong> — la TVA n'est pas une recette, et
          les charges sont elles aussi en HT. CA des commandes marquées
          « Livrée », transport et manutention comprises : une livraison faite
          mais pas encore clôturée n'y est pas encore.
        </div>
        </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ObjectifMois;
